/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Guest } from '../../core/types/guest.type';
import { RoomReservation } from '../../core/types/room-reservation.type';
import { ConfigResponse } from '../../core/types/config-response.type';
import { CommonModule } from '@angular/common';
import { PrivacyItem } from '../../core/types/privacy-item.type';
import { FormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import { StorageService } from '../../services/storage.service';
import { Vehicle } from '../../core/types/vehicle.type';
import { VehicleType } from '../../core/types/vehicle-type.enum';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-pdf-component',
  templateUrl: './pdf-component.component.html',
  styleUrls: ['./pdf-component.component.css'],
  imports: [CommonModule, TranslateModule],
  standalone: true,
})
export class PdfComponentComponent implements OnChanges, OnInit  {
  @Input() guest!: Guest | null;
  @Input() reservation!: RoomReservation;
  @Input() config!: ConfigResponse | null;
  @Input() privacyItems!: PrivacyItem[];
  @Input() form!: FormGroup;
  @Input() formValues!: any;

  subscription: Subscription = new Subscription();
  pdfFile!: Blob;
  today = new Date();
  isForPdf = false;
  selectedValues = [];
  logoDataUrl!: string | null;
  signatureDataUrl!: string | null;
  doc!: jsPDF;
  errorObj: any;
  private readonly bottomMargin = 20;
  private readonly topMargin = 20;
  imgWidth: number = 0;
  imgHeight: number = 0;
  startX: number;
  startY: number;
  pageWidth = 0;
  rowHeight: number;

  constructor(
    private cd: ChangeDetectorRef,
    private tr: TranslateService,
    private storageService: StorageService,
  ) {
    this.errorObj = {};
    this.rowHeight = 8;
    this.startX = 10;
    this.startY = 20;
    this.doc = new jsPDF();
  }

  async ngOnInit(): Promise<void> {
    this.logoDataUrl = this.storageService.readFromSessionStorage('logoDataUrl');

    this.pageWidth = this.doc.internal.pageSize.getWidth() - (this.startX * 2);
    this.doc.setFont('RobotoRegular', 'normal');

    this.doc.addFont('../../../assets/fonts/Roboto/Roboto-Black.ttf', 'Roboto-Black', 'bold');
    this.doc.addFont('../../../assets/fonts/Roboto/Roboto-Regular.ttf', 'RobotoRegular', 'normal');

    this.doc.setFont('Roboto-Black', 'bold');
    this.doc.setFontSize(12);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formValues'] && changes['formValues'].currentValue) {
      this.isForPdf = true;
      this.cd.detectChanges();
    }
  }

  async generatePDF(): Promise<{ doc: Blob; hasPdfError: boolean; errorObj: any }> {
    this.signatureDataUrl = this.storageService.readFromSessionStorage('signatureDataUrl');

    const labels = this.getLabels();
    const data = this.getData();
    let hasPdfError = false;

    // Logo
    hasPdfError = !this.tryAddLogo() || hasPdfError;

    // Titolo
    const startXTitle = hasPdfError ? this.startX : this.startX + 110;
    const align = hasPdfError ? 'left' : 'center';
    this.startY = this.writeTitle(this.formValues['propertyName'], startXTitle, this.startY, align, this.rowHeight);

    // Dettagli
    this.startY = this.writeDetails(labels, data, this.startX, this.startY);

    // Privacy item iniziale
    const privacyItems = this.config?.privacyItems || [];
    const privacyItemsData = this.formValues['acceptedPrivacyItems'] || [];
    const splittedTextMainPrivacy = this.doc.splitTextToSize(privacyItems[0].text, this.pageWidth);
    const firstPrivacyItemHeight = splittedTextMainPrivacy.length * this.rowHeight - 5;
    this.startY = this.checkAddPage(this.startY, firstPrivacyItemHeight);
    this.startY = this.startY !== this.topMargin ? this.startY + this.rowHeight : this.topMargin;
    this.doc.text(splittedTextMainPrivacy, this.startX, this.startY);
    this.startY += firstPrivacyItemHeight;

    // Privacy items (con firma accodata all'ultimo)
    let signatureAlreadyAccounted = false;

    this.startY = this.writePrivacyItems(
      privacyItemsData,
      privacyItems,
      this.startX,
      this.startY
    );

    // Se l'ultimo privacy item era presente e la firma è stata già considerata, NON fare altri check
    const lastItem = privacyItemsData[privacyItemsData.length - 1];
    if (lastItem) {
      signatureAlreadyAccounted = true;
    }

    hasPdfError = !this.tryLoadSignature() || hasPdfError;
    this.startY = this.writeSignatureAndDate(this.startX, this.startY, signatureAlreadyAccounted);

    // Numeri di pagina
    this.writePagination();

    const uri = this.doc.output('datauristring');
    this.storageService.writeToSessionStorage('pdfDataUrl', uri);
    this.storageService.writeToSessionStorage('hasPdfError', JSON.stringify(hasPdfError));

    return {
      doc: this.doc.output('blob'),
      hasPdfError,
      errorObj: this.errorObj,
    };
  }

  private tryAddLogo(): boolean {
    try {
      const logoDataUrl = this.storageService.readFromSessionStorage('logoDataUrl');
      if (!logoDataUrl) throw new Error('Logo not found');
      const img = new Image();
      img.src = logoDataUrl;
      img.crossOrigin = 'anonymous';
      const { width, height } = this.doc.getImageProperties(img);
      this.doc.addImage(logoDataUrl, 'PNG', 0, 0, width / 4, height / 4, 'logo');
      return true;
    } catch (err) {
      console.error('Error adding logo to PDF:', err);
      this.errorObj = { ...this.errorObj, logo: err };
      return false;
    }
  }

  private writeTitle(title: string, x: number, y: number, align: 'left' | 'center', rowHeight: number): number {
    this.doc.text(title, x, y, { align });
    y += rowHeight;
    this.doc.text(this.tr.instant('pdf.title'), x, y, { align });
    return y + 30;
  }

  private writeDetails(labels: string[], data: any[], x: number, y: number): number {
    let line = 0;
    this.doc.setFont('RobotoRegular', 'normal');

    for (let i = 0; i < labels.length; i++) {
      const value = data[i];
      const isArray = Array.isArray(value);

      if (
        (!isArray && value) ||
        (isArray && this.hasVehiclesArrayData(value))
      ) {
        this.doc.text(labels[i], x, y + line * this.rowHeight);
        line++;
      }
    }

    line = 0;
    for (let i = 0; i < data.length; i++) {
      if (!data[i]) continue;

      const label = labels[i];
      const value = data[i];

      if (label === this.tr.instant('privacy_form.guest_details.vehicles')) {
        const vehicles: Vehicle[] = this.formValues['vehicles'];
        vehicles.forEach((v) => {
          if (!v.plateCode) return;
          const plateCode = `${this.tr.instant('privacy_form.guest_details.plateCode')}: ${v.plateCode?.toUpperCase()}`;
          const vehicleTypeName = this.translateVehicleTypeId(v.typeId || VehicleType.AUTO);
          if (plateCode && vehicleTypeName) {
            this.doc.text(this.doc.splitTextToSize(vehicleTypeName, 50), 70, y + line * this.rowHeight);
            this.doc.text(this.doc.splitTextToSize(plateCode, 80), 120, y + line * this.rowHeight);
            line++;
          }
        });
        break;
      } else {
        const text = this.doc.splitTextToSize(value, 200);
        this.doc.text(text, 70, y + line * this.rowHeight);
        line++;
      }
    }

    return y + line * this.rowHeight;
  }

  hasVehiclesArrayData(data: any): boolean {
    return data.filter((v: Vehicle) => v.plateCode).length > 0;
  }

  private writePrivacyItems(
    acceptedItems: any[],
    privacyItems: PrivacyItem[],
    x: number,
    y: number
  ): number {
    for (let i = 1; i < acceptedItems.length; i++) {
      const isLast = i === acceptedItems.length - 1;
      const formItem = acceptedItems[i];
      const item = privacyItems.find(p => p.id === formItem.id);
      if (!item) continue;

      const label = formItem.options?.find((o: any) => o.value === formItem.value)?.label || 'NA';
      const splittedText = this.doc.splitTextToSize(item.text, this.pageWidth);
      const labelHeight = 10 + this.rowHeight;
      const textHeight = splittedText.length * this.rowHeight;

      let footerHeight = 0;
      if (isLast && this.signatureDataUrl) {
        footerHeight = this.rowHeight * 2 + this.imgHeight / 4 + 20;
      }

      const totalHeight = labelHeight + textHeight + footerHeight;

      // Check if everything fits together
      y = this.checkAddPage(y, totalHeight);

      // Add label
      this.doc.setFont('Roboto-Black', 'bold');
      this.doc.text(label, x, y + 10);
      y += labelHeight;

      // Add text
      this.doc.setFont('RobotoRegular', 'normal');
      this.doc.text(splittedText, x, y);
      y += textHeight;
    }

    return y;
  }

  private tryLoadSignature(): boolean {
    try {
      if (!this.signatureDataUrl) return false;
      const img = new Image();
      img.src = this.signatureDataUrl;
      img.crossOrigin = 'anonymous';
      const size = this.doc.getImageProperties(img);
      this.imgWidth = size.width;
      this.imgHeight = size.height;
      return true;
    } catch (err) {
      console.error('Error loading signature image:', err);
      this.errorObj = { ...this.errorObj, signature: err };
      return false;
    }
  }

  private writeSignatureAndDate(x: number, y: number, skipCheck = false): number {
    if (!this.signatureDataUrl) return y;

    const signatureHeight = this.rowHeight * 2 + this.imgHeight / 4 + 20;

    if (!skipCheck) {
      y = this.checkAddPage(y, signatureHeight);
    }

    y += this.rowHeight;

    const city = this.formValues['_locationCity'] || '';
    const date = new Date().toLocaleDateString('en-GB');
    this.doc.text(`${city}, ${date}`, x + 100, y);

    this.doc.addImage(
      this.signatureDataUrl,
      'PNG',
      100,
      y + this.rowHeight,
      this.imgWidth / 4,
      this.imgHeight / 4,
      'Signature'
    );
    return y + this.imgHeight / 4 + this.rowHeight * 2;
  }

  private writePagination() {
    const pageCount = this.doc.getNumberOfPages();

    if (pageCount <= 1) return;
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      this.doc.setFontSize(10);
      this.doc.setFont('RobotoRegular', 'normal');

      const pageText = `${i} / ${pageCount}`;
      const pageWidth = this.doc.internal.pageSize.getWidth();
      const pageSize = this.doc.internal.pageSize;
      const y = ((typeof pageSize.getHeight === 'function' ? pageSize.getHeight() : pageSize.height) || 10) - 10;

      const x = pageWidth / 2;
      this.doc.text(pageText, x, y, { align: 'center' });
    }
  }

  private getData() {
    return [
      this.formValues['checkinNumber'] || '',
      this.formValues['firstName'] + ' ' + this.formValues['lastName'],
      this.formValues['email'] || '',
      this.formValues['phone'] || '',
      this.formValues['roomName'] || '',
      this.formValues['checkinDate'] + ' - ' + this.formValues['checkoutDate'],
      this.formValues['adults']
        ? this.formValues['adults'] + ' ' + this.tr.instant('privacy_form.guest_details.adults')
        : '' + this.formValues['children']
          ? ' , ' + this.formValues['children'] + ' ' + this.tr.instant('privacy_form.guest_details.children')
          : '',
      this.formValues['cityTaxAmount'] ? this.formValues['cityTaxAmount'] + this.formValues['currencyCode'] : '',
      this.formValues['_facilities'],
      this.formValues['vehicles'] || [],
    ];
  }

  private translateVehicleTypeId(typeId: VehicleType) {
    const vehicleTypes = Object.keys(VehicleType) as Array<keyof typeof VehicleType>;
    const typeSelected = vehicleTypes.find((key) => VehicleType[key] == typeId);
    return typeSelected;
  }

  private getLabels() {
    const checkin_number = this.tr.instant('privacy_form.guest_details.checkin_number');
    const fullName = this.tr.instant('privacy_form.guest_details.name');
    const email = this.tr.instant('privacy_form.guest_details.email');
    const phone = this.tr.instant('privacy_form.guest_details.phone');
    const room = this.tr.instant('privacy_form.guest_details.room');
    const stay = this.tr.instant('privacy_form.guest_details.stay');
    const composition = this.tr.instant('privacy_form.guest_details.composition');
    const cityTax = this.tr.instant('privacy_form.guest_details.city_tax');
    const facilities = this.tr.instant('privacy_form.guest_details.facilities');
    const vehicles = this.tr.instant('privacy_form.guest_details.vehicles');
    const adults = this.tr.instant('privacy_form.guest_details.adults');
    return [
      checkin_number,
      fullName,
      email,
      phone,
      room,
      stay,
      composition,
      cityTax,
      facilities,
      vehicles,
      adults,
    ];
  }

  private checkAddPage(currentY: number, nextContentHeight: number = 8): number {
    const pageSize = this.doc.internal.pageSize;
    const pageHeight = typeof pageSize.height !== 'undefined' ? pageSize.height : 297; // default A4

    if (currentY + nextContentHeight > pageHeight - this.bottomMargin) {
      this.doc.addPage();
      return this.topMargin;
    }
    return currentY;
  }
}
